import { useEffect, useState } from 'react';
import { useIsMobile } from 'lib/hooks/useIsMobile';

interface DialogPosition {
  top: number;
  left: number;
}

interface DialogPositioningState {
  dialogPosition: DialogPosition;
}

interface UseDialogPositioningProps {
  isOpen: boolean;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  position: 'right' | 'bottom';
  dialogWidth?: number;
  dialogHeight?: number;
  margin?: number;
}

export function useDialogPositioning({
  isOpen,
  buttonRef,
  position,
  dialogWidth = 199, // Match the width from CSS
  dialogHeight = 200, // Approximate dialog height
  margin = 8, // Margin from the button
}: UseDialogPositioningProps): DialogPositioningState {
  const [dialogPosition, setDialogPosition] = useState<DialogPosition>({ top: 0, left: 0 });
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isOpen || !buttonRef.current) {
      return;
    }

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let leftPosition = 0;
    let topPosition = 0;

    if (position === 'right') {
      // Always position to the right of the button
      leftPosition = buttonRect.width + margin;
      topPosition = 0;
    } else if (position === 'bottom') {
      // Position below the button
      topPosition = buttonRect.height + margin;

      if (isMobile) {
        // In mobile view, automatically choose left or right based on button position
        const buttonCenterX = buttonRect.left + buttonRect.width / 2;
        const viewportCenterX = viewportWidth / 2;

        if (buttonCenterX < viewportCenterX) {
          // Button is on the left side, position dialog to the left
          leftPosition = 0;
        } else {
          // Button is on the right side, position dialog to the right
          leftPosition = buttonRect.width - dialogWidth;
        }
      } else {
        // Desktop view: center horizontally
        leftPosition = (buttonRect.width - dialogWidth) / 2;
      }

      // Check if dialog would extend beyond viewport boundaries
      const dialogLeftEdge = buttonRect.left + leftPosition;
      const dialogRightEdge = dialogLeftEdge + dialogWidth;

      // If dialog extends beyond right edge, adjust position
      if (dialogRightEdge > viewportWidth) {
        leftPosition = viewportWidth - buttonRect.left - dialogWidth - margin;
      }

      // If dialog extends beyond left edge, adjust position
      if (dialogLeftEdge < margin) {
        leftPosition = margin - buttonRect.left;
      }

      // Ensure dialog doesn't go below viewport
      const dialogBottomEdge = buttonRect.bottom + topPosition + dialogHeight;
      if (dialogBottomEdge > viewportHeight) {
        // Position above the button instead
        topPosition = -dialogHeight - margin;
      }
    }

    // Set dialog position
    setDialogPosition({
      top: topPosition,
      left: leftPosition,
    });
  }, [isOpen, buttonRef, position, isMobile, dialogWidth, dialogHeight, margin]);

  return {
    dialogPosition,
  };
}
